/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState, type ReactElement } from "react";
import { ConnectionQuality, RoomEvent, type Room } from "livekit-client";

import { useSetting } from "./settings";
import {
  callQuality,
  cameraResolution,
  dataSaver,
  defaultCallType,
  echoCancellation,
  noiseSuppression,
  shareOnlineStatus,
  totpSecret,
  userStatus,
  userStatusMessage,
} from "../meet/MeetSettings";
import styles from "./MeetSettings.module.css";

interface Props {
  rooms?: { room: Room }[];
}

interface BrowserNet {
  online: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

interface OSInfo {
  os: string;
  browser: string;
  cores: number | string;
  memory: string;
  touch: boolean;
  language: string;
  screen: string;
  viewport: string;
}

function getOSInfo(): OSInfo {
  const ua = navigator.userAgent;
  const uaData = (
    navigator as unknown as { userAgentData?: { platform?: string } }
  ).userAgentData;
  let os = "Unknown";
  if (uaData?.platform) {
    os = uaData.platform;
  } else if (/Windows/i.test(ua)) {
    os = "Windows";
  } else if (/Mac/i.test(ua)) {
    os = "macOS";
  } else if (/Android/i.test(ua)) {
    os = "Android";
  } else if (/iPhone|iPad|iPod/i.test(ua)) {
    os = "iOS";
  } else if (/Linux/i.test(ua)) {
    os = "Linux";
  }

  let browser = "Unknown";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/Chrome\//i.test(ua)) browser = "Chrome";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Safari\//i.test(ua)) browser = "Safari";

  const memory = (
    navigator as unknown as { deviceMemory?: number }
  ).deviceMemory;
  const touch =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  return {
    os,
    browser,
    cores: navigator.hardwareConcurrency ?? "—",
    memory: typeof memory === "number" ? `${memory} GB` : "—",
    touch,
    language: navigator.language,
    screen: `${window.screen.width}×${window.screen.height}`,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
  };
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

interface CallStats {
  level: ConnectionQuality | "lost";
  uploadMbps: number;
  downloadMbps: number;
  pingMs: number;
  inCall: boolean;
}

function readBrowserNet(): BrowserNet {
  const conn = (
    navigator as unknown as { connection?: BrowserNet & EventTarget }
  ).connection;
  return {
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    effectiveType: conn?.effectiveType,
    downlink: conn?.downlink,
    rtt: conn?.rtt,
  };
}

function useBrowserNet(): BrowserNet {
  const [net, setNet] = useState<BrowserNet>(readBrowserNet);
  useEffect(() => {
    const update = (): void => setNet(readBrowserNet());
    const conn = (
      navigator as unknown as { connection?: BrowserNet & EventTarget }
    ).connection;
    conn?.addEventListener?.("change", update);
    const on = (): void => setNet((n) => ({ ...n, online: true }));
    const off = (): void => setNet((n) => ({ ...n, online: false }));
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      conn?.removeEventListener?.("change", update);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return net;
}

function levelLabel(q: ConnectionQuality | "lost"): string {
  if (q === "lost") return "No connection";
  switch (q) {
    case ConnectionQuality.Excellent:
      return "Excellent";
    case ConnectionQuality.Good:
      return "Good";
    case ConnectionQuality.Poor:
      return "Poor";
    default:
      return "Lost";
  }
}

function useCallStats(room: Room | undefined): CallStats {
  const [stats, setStats] = useState<CallStats>({
    level: "lost",
    uploadMbps: 0,
    downloadMbps: 0,
    pingMs: 0,
    inCall: false,
  });

  useEffect(() => {
    if (!room) {
      setStats({
        level: "lost",
        uploadMbps: 0,
        downloadMbps: 0,
        pingMs: 0,
        inCall: false,
      });
      return;
    }

    const updateLevel = (q: ConnectionQuality): void =>
      setStats((s) => ({ ...s, level: q, inCall: true }));
    room.on(RoomEvent.ConnectionQualityChanged, updateLevel);
    updateLevel(room.localParticipant.connectionQuality);

    let cancelled = false;
    const last = { bytesSent: 0, bytesRecv: 0, ts: 0 };
    const poll = async (): Promise<void> => {
      const engine = (room as unknown as { engine?: { publisher?: { pc?: RTCPeerConnection } } })
        .engine;
      const pc = engine?.publisher?.pc;
      if (!pc) return;
      let report: RTCStatsReport;
      try {
        report = await pc.getStats();
      } catch {
        return;
      }
      if (cancelled) return;
      let bytesSent = 0;
      let bytesRecv = 0;
      let pingMs = 0;
      let hasPair = false;
      report.forEach((s) => {
        if (s.type === "outbound-rtp") bytesSent += s.bytesSent ?? 0;
        else if (s.type === "inbound-rtp") bytesRecv += s.bytesReceived ?? 0;
        else if (
          s.type === "candidate-pair" &&
          (s as { state?: string }).state === "succeeded" &&
          (s as { nominated?: boolean }).nominated
        ) {
          hasPair = true;
          const rtt = (s as { currentRoundTripTime?: number })
            .currentRoundTripTime;
          if (typeof rtt === "number") pingMs = Math.round(rtt * 1000);
        }
      });
      const now = performance.now();
      if (last.ts > 0) {
        const dt = (now - last.ts) / 1000;
        if (dt > 0) {
          setStats((s) => ({
            ...s,
            uploadMbps: Math.max(0, ((bytesSent - last.bytesSent) * 8) / 1e6 / dt),
            downloadMbps: Math.max(0, ((bytesRecv - last.bytesRecv) * 8) / 1e6 / dt),
            pingMs: hasPair ? pingMs : s.pingMs,
            inCall: true,
          }));
        }
      }
      last.bytesSent = bytesSent;
      last.bytesRecv = bytesRecv;
      last.ts = now;
    };

    const id = window.setInterval(() => void poll(), 2000);
    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(id);
      room.off(RoomEvent.ConnectionQualityChanged, updateLevel);
    };
  }, [room]);

  return stats;
}

export const StatusSettingsTab: FC<Props> = ({ rooms }) => {
  const net = useBrowserNet();
  const callRoom = rooms?.[0]?.room;
  const call = useCallStats(callRoom);

  const os = useState<OSInfo>(getOSInfo)[0];
  const mountedAt = useState<number>(() => Date.now())[0];
  const [now, setNow] = useState<number>(() => Date.now());
  const [callStart, setCallStart] = useState<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (callRoom && callStart === null) setCallStart(Date.now());
    if (!callRoom && callStart !== null) setCallStart(null);
  }, [callRoom, callStart]);

  const tabOpenMs = now - mountedAt;
  const callMs = callStart !== null ? now - callStart : 0;

  const [shareOnline] = useSetting(shareOnlineStatus);
  const [presence] = useSetting(userStatus);
  const [presenceMsg] = useSetting(userStatusMessage);
  const [twoFa] = useSetting(totpSecret);
  const [defType] = useSetting(defaultCallType);
  const [res] = useSetting(cameraResolution);
  const [quality] = useSetting(callQuality);
  const [dataSaverOn] = useSetting(dataSaver);
  const [echo] = useSetting(echoCancellation);
  const [noise] = useSetting(noiseSuppression);

  const resolutionLabel: Record<string, string> = {
    "4320": "8K UHD (4320p)",
    "2160": "4K UHD (2160p)",
    "1440": "1440p QHD",
    "11264": "11K Ultra (11264p)",
    "1080": "1080p FHD",
    "720": "720p HD",
    "480": "480p",
    "360": "360p",
  };

  const typeLabel: Record<string, string> = {
    meet: "Meet (group video)",
    coffee: "Coffee (audio only)",
    video: "Video (HD point-to-point)",
  };

  return (
    <div>
      <h3 className={styles.sectionTitle}>Connection</h3>
      <div className={styles.statusGrid}>
        <Row k="Online" v={net.online ? "Yes" : "No"} />
        <Row
          k="Network"
          v={
            net.effectiveType
              ? net.effectiveType.toUpperCase()
              : net.online
                ? "Online"
                : "Offline"
          }
        />
        <Row
          k="Downlink"
          v={typeof net.downlink === "number" ? `${net.downlink.toFixed(1)} Mb/s` : "—"}
        />
        <Row
          k="Browser ping"
          v={typeof net.rtt === "number" && net.rtt > 0 ? `${net.rtt} ms` : "—"}
        />
      </div>

      <h3 className={styles.sectionTitle}>In call</h3>
      {call.inCall ? (
        <div className={styles.statusGrid}>
          <Row k="My quality" v={levelLabel(call.level)} />
          <Row k="Ping" v={call.pingMs > 0 ? `${call.pingMs} ms` : "—"} />
          <Row k="Upload" v={`${call.uploadMbps.toFixed(1)} Mb/s`} />
          <Row k="Download" v={`${call.downloadMbps.toFixed(1)} Mb/s`} />
          <Row k="Call time" v={formatDuration(callMs)} />
        </div>
      ) : (
        <p className={styles.small}>
          Not in a call. Join a call to see live quality and ping.
        </p>
      )}

      <h3 className={styles.sectionTitle}>Device</h3>
      <div className={styles.statusGrid}>
        <Row k="Operating system" v={os.os} />
        <Row k="Browser" v={os.browser} />
        <Row k="CPU cores" v={String(os.cores)} />
        <Row k="Device memory" v={os.memory} />
        <Row k="Touch screen" v={os.touch ? "Yes" : "No"} />
        <Row k="Language" v={os.language} />
        <Row k="Screen" v={os.screen} />
        <Row k="Viewport" v={os.viewport} />
      </div>

      <h3 className={styles.sectionTitle}>Uptime</h3>
      <div className={styles.statusGrid}>
        <Row k="My Status open" v={formatDuration(tabOpenMs)} />
        {call.inCall && <Row k="Call time" v={formatDuration(callMs)} />}
      </div>

      <h3 className={styles.sectionTitle}>Account</h3>
      <div className={styles.statusGrid}>
        <Row k="Share online status" v={shareOnline ? "On" : "Off"} />
        <Row k="Presence" v={presence} />
        <Row k="Status message" v={presenceMsg || "—"} />
        <Row k="Local 2FA" v={twoFa ? "Enabled" : "Disabled"} />
      </div>

      <h3 className={styles.sectionTitle}>Media</h3>
      <div className={styles.statusGrid}>
        <Row k="Default call type" v={typeLabel[defType] ?? defType} />
        <Row k="Camera resolution" v={resolutionLabel[res] ?? `${res}p`} />
        <Row k="Call quality" v={quality} />
        <Row k="Data saver" v={dataSaverOn ? "On" : "Off"} />
        <Row k="Echo cancellation" v={echo ? "On" : "Off"} />
        <Row k="Noise suppression" v={noise ? "On" : "Off"} />
      </div>

      <h3 className={styles.sectionTitle}>Security</h3>
      <div className={styles.statusGrid}>
        <Row k="End-to-end encryption" v="On (always)" />
      </div>
    </div>
  );
};

function Row({ k, v }: { k: string; v: string }): ReactElement {
  return (
    <div className={styles.statusRow}>
      <span className={styles.statusKey}>{k}</span>
      <span className={styles.statusVal}>{v}</span>
    </div>
  );
}
